import { useCallback, useMemo, useState } from "react";
import {
  BooleanFilterPair,
  EMPTY_FILTERS,
  ProgrammingStructuresFilters,
  ProgrammingStructuresSearchParams,
  SelectedSubject,
  StructureSourceCode,
} from "@services/types/programming-structures/programmingStructuresFiltersTypes";
import {
  hasAnySelection,
  parseFiltersFromUrl,
  resolvePrimaryActionLabel,
  serializeFilters,
  toggleExclusivePair,
  toggleExclusiveSource,
} from "@utils/programmingStructures";

interface UseProgrammingStructuresFiltersParams {
  /** Query string used to hydrate both states on mount. */
  initialSearch: string;
  /** Whether the advanced panel is currently expanded. */
  isAdvancedPanelOpen: boolean;
}

const useProgrammingStructuresFilters = ({
  initialSearch,
  isAdvancedPanelOpen,
}: UseProgrammingStructuresFiltersParams) => {
  const initialParams = useMemo<ProgrammingStructuresSearchParams>(
    () => parseFiltersFromUrl(initialSearch),
    // Hydration happens once; later URL writes are an effect of fetching, not a source.
    [],
  );

  const [form, setForm] = useState<ProgrammingStructuresFilters>(initialParams.filters);
  const [applied, setApplied] = useState<ProgrammingStructuresFilters>(initialParams.filters);

  const setSubject = useCallback(
    (subject: SelectedSubject | null) => setForm(current => ({ ...current, subject })),
    [],
  );

  const setStructureCode = useCallback(
    (structureCode: string) => setForm(current => ({ ...current, structureCode })),
    [],
  );

  const toggleSource = useCallback(
    (code: StructureSourceCode) =>
      setForm(current => ({ ...current, sources: toggleExclusiveSource(current.sources, code) })),
    [],
  );

  const togglePublished = useCallback(
    (key: keyof BooleanFilterPair) =>
      setForm(current => ({
        ...current,
        isPublished: toggleExclusivePair(current.isPublished, key),
      })),
    [],
  );

  const toggleVisible = useCallback(
    (key: keyof BooleanFilterPair) =>
      setForm(current => ({ ...current, isVisible: toggleExclusivePair(current.isVisible, key) })),
    [],
  );

  const resetForm = useCallback(() => setForm(EMPTY_FILTERS), []);

  const isPending = useMemo(
    () => serializeFilters(form) !== serializeFilters(applied),
    [form, applied],
  );

  /**
   * Deliberately not "a request has been made": the unfiltered load on mount
   * must not count, otherwise the first interaction would already read
   * "Actualizar resultados" instead of "Aplicar filtros".
   */
  const hasAppliedSearch = useMemo(() => hasAnySelection(applied), [applied]);

  const hasSelection = useMemo(() => hasAnySelection(form), [form]);

  const primaryActionLabel = useMemo(
    () => resolvePrimaryActionLabel({ form, applied, isAdvancedPanelOpen }),
    [form, applied, isAdvancedPanelOpen],
  );

  /** Promotes the edited filters to applied and hands them back to the caller to fetch. */
  const applyFilters = useCallback(() => {
    setApplied(form);

    return form;
  }, [form]);

  /** Clears both states and hands back the empty set so the caller can refetch immediately. */
  const clearFilters = useCallback(() => {
    setForm(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);

    return EMPTY_FILTERS;
  }, []);

  return {
    form,
    applied,
    initialSort: initialParams.sort,
    initialPage: initialParams.page,
    setSubject,
    setStructureCode,
    toggleSource,
    togglePublished,
    toggleVisible,
    resetForm,
    isPending,
    hasAppliedSearch,
    hasSelection,
    primaryActionLabel,
    applyFilters,
    clearFilters,
  };
};

export default useProgrammingStructuresFilters;
